import { AuthenticatedUser } from "../models/users/AuthenticatedUser";
import { TableButtonActionSettings } from "../models/common/DataTable";
import { Nullable } from "../models/common/Nullable";
import {Module} from "../models/common/Module";
import {ACCOUNTING_AREA_TYPE_NAME} from "../models/accounting/AccountBook";

/**
 * build table button action settings based on the authenticated user
 */
export const buildTableButtonActionSettings = ( authUser: Nullable<AuthenticatedUser>, area: string, viewIdentifier: string ): TableButtonActionSettings => {
    if( authUser === null ) {
        return {};
    }
    
    return {
        //isDownloadable: isPrivileged( authUser, area, viewIdentifier, Privileges.download ), 
        isEditable: isPrivileged( authUser, area, viewIdentifier, Privileges.update ),
        isDeleteable: isPrivileged( authUser, area, viewIdentifier, Privileges.delete )   
    }
};

/**
 * function to check if the user has the neccessary rights to access the given functionality/privilege
 *
 * to create a full privilege string to check we need three parts
 * <area identifier>.<view/module identifier>.<privilege identifier>
 *
 * Examples:
 * Timesheet create new entry
 * timesheet.timesheet.create
 *
 * Project stage update
 * project.project_stages.update
 *
 * IMPORTANT:
 * every privilege MUST BE checked on the backend as well!
 * Client side privilege checking is only to display a coherent ui and don't
 * show the user too many elements he can't use.
 *
 * @param authUser the currently logged in user or null if not logged in
 * @param area the area the privilege belongs to
 * @param view the view the privilege belongs to
 * @param privilege the privilege to check for. example: ACCESS to enter a site
 * @see Privileges
 * @param privilegeIdentifier when given will always be used to check the privilege. to create a privilege identifier you can use
 * @see createPrivilegeIdentifier
 */
export function isPrivileged(
    authUser: Nullable< AuthenticatedUser >,
    area: string,
    view:string,
    privilege: string
): boolean {
    return isPrivilegedByIdentifier(
        authUser,
        createPrivilegeIdentifier( area, view, privilege)
    );
}

/**
 *
 * @param authUser
 * @param privilegeIdentifier when given will always be used to check the privilege. to create a privilege identifier you can use
 * @see createPrivilegeIdentifier
 */
export function isPrivilegedByIdentifier(
    authUser: Nullable< AuthenticatedUser >,
    privilegeIdentifier: string
): boolean {

    return authUser?.generalPrivileges[ privilegeIdentifier ]?.isPermitted ?? false;
}

/**
 * concatenates the three params to build a privilegeIdentifier.
 * for instructions how to construct a privilegeIdentifier and further information about the privilege system
 * @see isPrivileged
 *
 * @param area
 * @param view
 * @param privilege
 * @returns { string }
 */
export function createPrivilegeIdentifier(
    area: string,
    view:string,
    privilege: string
): string {
    return `${area}.${view}.${privilege}`.trim().toUpperCase();
}

/**
 * This function returns true, when at least ONE of all given privileges is true, use with care!
 *
 * @param authUser
 * @param module The isPrivileged function will be called on this module to test the given privileges
 * @param privileges
 */
export function isPrivilegedOneOf(
    authUser: Nullable< AuthenticatedUser >,
    module: Module,
    privileges: string[]
): boolean {

    return privileges.some( privilege => module.isPrivileged( authUser, privilege ) );
}

/**
 * some standard privileges that are commonly used on many views
 * this is only the last part of of the privilege. the other parts depend on the area and module it is used in
 *
 * @see isPrivileged
 */
export const Privileges = {
    access: 'ACCESS',
    create: 'CREATE',
    update: 'UPDATE',
    delete: 'DELETE',
    restore: 'RESTORE',
    accessOwn: 'ACCESS_OWN',
    createOwn: 'CREATE_OWN',
    updateOwn: 'UPDATE_OWN',
    deleteOwn: 'DELETE_OWN',
    restoreOwn: 'RESTORE_OWN',
    accessSensitiveData: 'ACCESS_SENSITIVE',
    download: 'DOWNLOAD',
    upload: 'UPLOAD',
        addUsersToUserGroup: 'can_add_users_to_user_group',
        deleteUserFromUserGroup: 'can_delete_users_from_user_group'
};